import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Trash2, Calendar, User, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import ConfirmationModal from '@/Components/common/ConfirmationModal';
import './ViewCertificates.css';

const ViewCertificates: React.FC = () => {
  const navigate = useNavigate();
  const {
    issuedCertificates,
    issuedCertificatesTotalPages,
    issuedCertificatesCurrentPage,
    loading,
    fetchIssuedCertificates,
    deleteCertificate,
  } = useCertificationManagementStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<number | null>(null);

  useEffect(() => {
    loadCertificates(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadCertificates = (page: number) => {
    fetchIssuedCertificates(page, 10, searchTerm);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadCertificates(1);
  };

  const handleDeleteClick = (certificateId: number) => {
    setCertificateToDelete(certificateId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (certificateToDelete) {
      const result = await deleteCertificate(certificateToDelete);
      if (result.success) {
        loadCertificates(currentPage);
      }
      setCertificateToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCertificateToDelete(null);
  };

  const handleView = (certificate: any) => {
    navigate(`/superadmin/certification-management/certificate/${certificate.id}`);
  };

  const handleNextPage = () => {
    if (currentPage < issuedCertificatesTotalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-view-certificates">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="admin-view-certificates-search">
        <input
          type="text"
          placeholder="Search by certificate name, number, or recipient..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button type="submit">Search</button>
      </form>

      {/* Certificates Grid */}
      {loading ? (
        <div className="admin-view-certificates-loading">
          <div className="spinner"></div>
          <p>Loading certificates...</p>
        </div>
      ) : issuedCertificates.length === 0 ? (
        <div className="admin-view-certificates-empty">
          <Award size={48} />
          <h3>No certificates found</h3>
          <p>Start by issuing a certificate to a user</p>
        </div>
      ) : (
        <>
          <div className="admin-view-certificates-grid">
            {issuedCertificates.map((certificate) => (
              <div key={certificate.id} className="admin-view-certificates-card">
                <div className="admin-view-certificates-card-header">
                  <Award size={20} className="admin-view-certificates-card-icon" />
                  <span
                    className={`admin-view-certificates-status admin-view-certificates-status-${certificate.status}`}
                  >
                    {certificate.status}
                  </span>
                </div>

                <h3 className="admin-view-certificates-card-title">{certificate.certificate_name}</h3>

                <p className="admin-view-certificates-card-number">#{certificate.certificate_number}</p>

                <div className="admin-view-certificates-card-info">
                  <div className="admin-view-certificates-card-info-item">
                    <User size={14} />
                    <span>
                      {certificate.first_name} {certificate.last_name}
                    </span>
                  </div>
                  <div className="admin-view-certificates-card-info-item">
                    <Calendar size={14} />
                    <span>{formatDate(certificate.issue_date)}</span>
                  </div>
                </div>

                {certificate.description && (
                  <p className="admin-view-certificates-card-desc">{certificate.description}</p>
                )}

                <div className="admin-view-certificates-card-actions">
                  <button
                    className="admin-view-certificates-view-btn"
                    onClick={() => handleView(certificate)}
                  >
                    <Eye size={16} />
                    View
                  </button>
                  <button
                    className="admin-view-certificates-delete-btn"
                    onClick={() => handleDeleteClick(certificate.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {issuedCertificatesTotalPages > 1 && (
            <div className="admin-view-certificates-pagination">
              <button
                className="admin-view-certificates-pagination-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <span className="admin-view-certificates-pagination-info">
                Page {currentPage} of {issuedCertificatesTotalPages}
              </span>

              <button
                className="admin-view-certificates-pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage === issuedCertificatesTotalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Certificate"
        message="Are you sure you want to delete this certificate? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ViewCertificates;
